import { ComponentPortal, DomPortalHost } from '@angular/cdk/portal';
import { formatDate } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  Inject,
  Injectable,
  Injector,
  LOCALE_ID,
  NgZone
} from '@angular/core';
import { Logger } from 'aws-amplify';
import { PDFDocument, PageSizes } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { Observable, from, iif, of, zip } from 'rxjs';
import { finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Doctor } from 'src/API';
import { FaxService } from 'src/app/core/api/fax.service';
import { ReferralFormGroup } from 'src/app/referral/referral-form/referral-form.model';
import { Clinic, ClinicService } from '../core/api/clinic.service';
import { Html2PdfService } from '../core/api/html-2-pdf.service';
import { UrlWithContentType } from '../shared/attachments/s3-attachment.service';
import { ContentType } from '../shared/shared-pipes/image-to-data-uri.pipe';
import { PDF } from './../core/api/html-2-pdf.service';
import { DrawSymptomService } from './../shared/symptoms/draw-symptom-control/draw-symptom.service';
import { ReferralPdfExportComponent } from './referral-pdf-export/referral-pdf-export.component';

@Injectable({
  providedIn: 'root'
})
export class ReferralPdfServiceService {
  private pdfReferralComponentPortal: ComponentPortal<ReferralPdfExportComponent>;
  private bodyPortalHost: DomPortalHost;

  private debugWindow: Window;

  private logger: Logger = new Logger('ReferralPdfServiceService');

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private html2PdfService: Html2PdfService,
    private clinicService: ClinicService,
    private faxService: FaxService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private ngZone: NgZone,
    private drawSymptomService: DrawSymptomService
  ) {
    this.setupReferralPortal();
  }

  public downloadPDFForReferral(
    referral: ReferralFormGroup,
    clinic: Clinic | Doctor['clinic'],
    preloadedAttachments: UrlWithContentType[],
    referralCenterResponse: UrlWithContentType[],
    debug: boolean = false
  ): Observable<boolean> {
    const filename = `referral_ ${this.getDateForPDFFilename()}.pdf`;
    return this.getHTMLForReferralBody(
      referral,
      clinic,
      preloadedAttachments,
      referralCenterResponse,
      debug
    ).pipe(
      mergeMap(html => {
        return zip(
          this.html2PdfService.html2Pdf(html),
          this.parseUrlWithContentType(preloadedAttachments),
          this.parseUrlWithContentType(referralCenterResponse)
        );
      }),
      mergeMap(([htmlBlob, imageBlob]) => {
        return from(this.appendImagesToPDF(htmlBlob, imageBlob));
      }),
      mergeMap(data => {
        const pdf: PDF = { __typename: 'PDF', base64: data };
        return this.html2PdfService.savePdfAs(pdf, filename);
      })
    );
  }

  private async appendImagesToPDF(existingPDF: PDF, imagesBase64: string[]) {
    const pdfToLoad = Uint8Array.from(atob(existingPDF.base64), c => c.charCodeAt(0));
    const pdfDoc = await PDFDocument.load(pdfToLoad);
    if (imagesBase64 !== null && imagesBase64 !== undefined) {
      for (const imageBase64 of imagesBase64) {
        const [imgType, imgBase64] = imageBase64.split(',');

        const imageByte = Uint8Array.from(atob(imgBase64), c => c.charCodeAt(0));
        const embeddedImage = imgType.includes('png')
          ? await pdfDoc.embedPng(imageByte)
          : await pdfDoc.embedJpg(imageByte);

        const page = pdfDoc.addPage(PageSizes.A4);
        let { width, height } = page.getSize();

        const imageSize = embeddedImage.scaleToFit(width, height);
        page.drawImage(embeddedImage, {
          x: 0,
          y: 2,
          width: imageSize.width,
          height: imageSize.height
        });
      }
    }
    const newPdfBytes = await pdfDoc.save();

    return this.arrayBufferToBase64(newPdfBytes);
  }
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
  // switchMap(html => this.html2PdfService.saveHtmlAsPdf(html, filename))

  public getHTMLForReferralBody(
    referral: ReferralFormGroup,
    clinic: Clinic | Doctor['clinic'],
    preloadedAttachments: UrlWithContentType[],
    referralCenterResponse: UrlWithContentType[],
    debug: boolean = false
  ): Observable<string> {
    if (debug) {
      this.openDebugWindow();
    }

    const leftEyeMarkupRawValue =
      referral.controls.signs &&
      referral.controls.signs.controls['LEFT_eyeMarkUp'] &&
      referral.controls.signs.controls['LEFT_eyeMarkUp'].value;
    const rightEyeMarkupRawValue =
      referral.controls.signs &&
      referral.controls.signs.controls['RIGHT_eyeMarkUp'] &&
      referral.controls.signs.controls['RIGHT_eyeMarkUp'].value;

    return iif(() => !!clinic, this.clinicService.getClinicLogo(clinic.id), of(null)).pipe(
      mergeMap(clinicLogo =>
        zip(
          leftEyeMarkupRawValue
            ? this.drawSymptomService.getImageUrlFromJSON(leftEyeMarkupRawValue)
            : of(null),
          rightEyeMarkupRawValue
            ? this.drawSymptomService.getImageUrlFromJSON(rightEyeMarkupRawValue)
            : of(null),
          of(clinicLogo)
        )
      ),
      switchMap(([leftEyeMarkup, rightEyeMarkup, clinicLogo]) => {
        const componentRef = this.bodyPortalHost.attach(this.pdfReferralComponentPortal);
        (componentRef.location.nativeElement as HTMLElement).style.display = 'none';
        componentRef.instance.data = referral;
        componentRef.instance.clinicLogo = clinicLogo;
        componentRef.instance.preloadedAttachments = preloadedAttachments;
        componentRef.instance.referralCenterResponse = referralCenterResponse;
        componentRef.instance.eyeMarkup = {
          left: leftEyeMarkup,
          right: rightEyeMarkup
        };

        return componentRef.instance.html.pipe(
          tap(html => {
            this.bodyPortalHost.detach();
            if (debug) {
              this.writeHTMLToDebugWindow(html);
            }
          })
        );
      })
    );
  }

  public preloadClinicLogo(clinic: Clinic | Doctor['clinic']) {
    if (clinic) {
      this.clinicService.getClinicLogo(clinic.id).subscribe();
    }
  }

  private getDateForPDFFilename(): string {
    return formatDate(new Date(), 'yyyy-mm-ddThh-mm-ss', this.locale);
  }

  private openDebugWindow() {
    this.debugWindow = window.open('', '_blank');
    this.debugWindow.document.title = 'CSI PDF Debug Loading...';
  }

  private writeHTMLToDebugWindow(html: string) {
    this.debugWindow.document.write(html);
    this.debugWindow.document.title = 'CSI PDF Debug';

    // For some reason the dry eye category component renders weird without this.
    // The 227px is the minimum but it can be set higher (lower values also cause
    // weird rendering). After 1 cycle we can clear the height (we do this
    // otherwise the body has no padding at the bottom).
    this.debugWindow.document.body.style.height = '227px';
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.debugWindow.document.body.style.height = '';
      });
    });
  }

  private setupReferralPortal() {
    this.pdfReferralComponentPortal = new ComponentPortal(ReferralPdfExportComponent);
    this.bodyPortalHost = new DomPortalHost(
      document.body,
      this.componentFactoryResolver,
      this.appRef,
      this.injector
    );
  }

  public parseUrlWithContentType(urlWithContentType: UrlWithContentType[]): Observable<string[]> {
    if (urlWithContentType.length < 1) return of(null);
    const renderedPDF$: Observable<string[]>[] = [];

    urlWithContentType.forEach(urlWithContent => {
      if (urlWithContent.contentType.includes(ContentType.PDF)) {
        const pdfBlobWithoutMIME = urlWithContent.url[
          'changingThisBreaksApplicationSecurity'
        ].split(',')[1];
        renderedPDF$.push(this.imagesFromPDF(pdfBlobWithoutMIME, ContentType.PDF));
      }
      if (urlWithContent.contentType.includes('image')) {
        let urlContent = urlWithContent.url['changingThisBreaksApplicationSecurity'];
        if (urlWithContent.contentType.includes('webp')) {
          renderedPDF$.push(
            this.convertToPNG(urlWithContent.url['changingThisBreaksApplicationSecurity']).pipe(
              map(dataUri => [dataUri])
            )
          );
        } else {

          renderedPDF$.push(of([urlContent]));
        }
      }
    });


    return zip(...renderedPDF$).pipe(
      map(renderedPages => {

        return renderedPages
          .reduce((acc, current) => [...acc, ...current], [])
          .filter(renderedPage => !!renderedPage);
      })
    );
  }

  public imagesFromPDF(base64: string, contentType?: ContentType): Observable<any[]> {
    if (!base64) {
      return of(null);
    }
    if (contentType) {
      if (contentType !== ContentType.PDF) return of(null);
    }
    const viewPortScale = 1.4;
    const blob = this.base64ToBlob(base64, contentType);

    const pdfUrl = window.URL.createObjectURL(blob);

    return from(
      Promise.all([import('pdfjs-dist/build/pdf'), import('pdfjs-dist/build/pdf.worker.entry')])
    ).pipe(
      mergeMap(async ([pdfjs, pdfjsWorker]) => {
        pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

        return pdfjsLib.getDocument(pdfUrl).promise;
      }),
      mergeMap(pdf => {
        const renderedPages$ = Promise.all(
          new Array(pdf.numPages).fill(0).map((page, pageNum) => {
            return new Promise(resolve =>
              pdf.getPage(pageNum + 1).then(data => {
                resolve(data);
              })
            ) as Promise<pdfjsLib.PDFPageProxy>;
          })
        );

        return renderedPages$;
      }),
      mergeMap(pages => {
        return Promise.all(
          pages.map(page => {
            return new Promise(resolve => {
              const viewport = page.getViewport({ scale: viewPortScale });
              const canvas = document.createElement('canvas');
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              const context = canvas.getContext('2d');

              const renderContext = {
                canvasContext: context,
                viewport: viewport,
                intent: 'print'
              };
              page.render(renderContext).promise.then(() => resolve(canvas.toDataURL()));
            });
          })
        );
      }),

      finalize(() => window.URL.revokeObjectURL(pdfUrl))
    );
  }

  public base64ToBlob(base64, contentType, filename?: string, createFile: boolean = false): Blob | null {
    if (!base64 || !contentType) {
      return null;
    }
    if (contentType !== ContentType.PDF) return null;
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });

    return (createFile) ? new File([blob], filename, { type: contentType }) :
      blob

  }

  // private calculateBockSize(height: number, width: number, viewportScale: number): number {
  //   const maxImageSize = 100;
  //   const bytesPerPixel = 3; //assuming we are using the 24bit color
  //   const estimateImageSizePerPage =
  //     (width * viewportScale * height * viewportScale * bytesPerPixel) / (1024 * 1024);

  //   let maxPerPagesPerBatch = Math.floor(maxImageSize / estimateImageSizePerPage);
  //   maxPerPagesPerBatch = Math.max(maxPerPagesPerBatch, 1);
  //   return maxPerPagesPerBatch;
  // }

  convertToPNG(urlContent: any): Observable<any> {
    const urlContentWithMimeType = urlContent.split(',');

    const base64 = urlContentWithMimeType[1];
    return from(
      new Promise((resolve, reject) => {
        const img = new Image();
        img.src = urlContent;
        img.onload = () => {

          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(blob => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
              resolve(reader.result);
            };
          }, 'image/png');
        };
      })
    );
  }
}
